import { get } from 'lodash'

import { BorderStyles, Colors, getServiceNameViaPoint, getServicePathToAPI, joinURLPath } from '@sigma-frontend-libs/commons'
import { Macro } from '@sigma-legacy-libs/cache'
import { isUUID } from '@sigmasms/commons'

import { buttonModes, components, icons, macroCacheOptions, numberToPxOrString, permissionPresets, placements, projectName, request, sizes } from '@/utils'

import render from './render'

const Cache = new Macro(macroCacheOptions)
const cachedGet = Cache.wrapWithCache(async (key, service, id, params) => {
  const pathToAPI = getServicePathToAPI(service)

  return await request.get(joinURLPath(pathToAPI, id), undefined, params?.query)
})

const className = 'sticker'

function resolveBadgePlacement(position) {
  const result = {
    [placements.top]: false,
    [placements.right]: false,
    [placements.bottom]: false,
    [placements.left]: false
  }

  if (position === 'left-top') {
    result[placements.left] = true
    result[placements.top] = true
  }

  if (position === 'right-top') {
    result[placements.right] = true
    result[placements.top] = true
  }

  if (position === 'right-bottom') {
    result[placements.right] = true
    result[placements.bottom] = true
  }

  if (position === 'left-bottom') {
    result[placements.left] = true
    result[placements.bottom] = true
  }

  return result
}

export default {
  name: 'Sticker',

  props: {
    value: {
      type: [ String, Number, Object ],
      default: () => {
        return undefined
      }
    },

    service: {
      type: String,
      default: 'users'
    },
    pathToId: {
      type: String,
      default: 'id'
    },
    pathToTitle: {
      type: String,
      default: 'title'
    },

    label: [ String, Number ],

    borderColor: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(Colors).includes(value)
      }
    },
    borderStyle: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(BorderStyles).includes(value)
      }
    },

    icon: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(icons).includes(value)
      }
    },
    iconSVG: {
      type: String,
      default: undefined
    },
    iconSize: {
      type: Number,
      default: undefined
    },
    iconColor: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(Colors).includes(value)
      }
    },
    iconTooltip: {
      type: String,
      default: undefined
    },

    tooltip: {
      type: String,
      default: undefined
    },
    tooltipTimeout: {
      type: Number,
      default: undefined
    },

    translatePath: {
      type: String,
      default: undefined
    },

    color: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(Colors).includes(value)
      }
    },
    labelColor: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(Colors).includes(value)
      }
    },
    backgroundColor: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(Colors).includes(value)
      }
    },

    badge: {
      type: Object,
      default: undefined
    },

    to: {
      type: Object,
      default: undefined
    },
    clickable: {
      type: Boolean,
      default: false
    },

    onClick: {
      type: Function,
      default: undefined
    },

    target: {
      type: String,
      default: undefined
    },

    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    },

    button: {
      type: Object,
      default: undefined
    },

    copy: {
      type: Boolean,
      default: false
    },

    sensitivity: {
      type: Boolean,
      default: false
    },

    minWidth: [ String, Number ],
    maxWidth: [ String, Number ],
    width: {
      type: [ String, Number ],
      default: 'auto'
    },

    progress: {
      type: Number,
      default: undefined
    }
  },

  data() {
    return {
      data: undefined,

      loadingGet: false,
      showTooltip: false,
      buttonCallback: false
    }
  },

  computed: {
    $_attrs() {
      return { target: this.target }
    },
    $_class() {
      return {
        [`${className}`]: true,

        [`${className}--icon`]: this.hasIcon,
        [`${className}--empty`]: !this.hasContent,
        [`${className}--link`]: this.hasTo || !!this.onClick || this.clickable,
        [`${className}--button`]: this.copy || !!this.button,
        [`${className}--after`]: !this.copy && !this.hasButton && this.hasAfter,
        [`${className}--loading`]: this.loadingGet,
        [`${className}--disabled`]: this.disabled,
        [`${className}--sensitivity`]: this.sensitivity,

        [`${className}--${this.color}`]: !!this.color,
        [`${className}-border--${this.borderColor}`]: !!this.borderColor,
        [`${className}-background--${this.backgroundColor}`]: !!this.backgroundColor
      }
    },
    $_style() {
      return {
        borderStyle: this.$_borderStyle,
        minWidth: numberToPxOrString(this.minWidth),
        maxWidth: numberToPxOrString(this.maxWidth),
        width: numberToPxOrString(this.width)
      }
    },
    $_labelClass() {
      return {
        [`${className}__label`]: true,
        [`${className}__label--${this.labelColor}`]: !!this.labelColor
      }
    },

    $_loading() {
      return this.loading || this.loadingGet
    },

    $_borderStyle() {
      if (this.borderStyle) {
        return this.borderStyle
      }
      if (this.hasTo || !!this.onClick || this.clickable) {
        return BorderStyles.solid
      }

      return BorderStyles.dashed
    },

    $_to() {
      if (this.readonly) {
        return
      }

      if (this.to) {
        return this.to
      }

      if (this.service && this.data) {
        const id = get(this.data, this.pathToId)
        if (id) {
          return {
            name: `${this.service}.single`,
            params: { id }
          }
        }
      }
    },

    $_button() {
      const result = {}
      if (this.button || this.copy) {
        result.size = sizes.small
        result.mode = buttonModes['flat-icon']

        if (this.button) {
          result.icon = this.button.icon
          result.iconSVG = this.button.iconSVG
          result.size = this.button.size || result.size
          result.mode = this.button.mode || result.mode
          result.color = this.button.color
          result.tooltip = this.button.tooltip
          result.loading = this.button.loading
          result.disabled = this.button.disabled
          result.callback = this.button.callback || this.button.on?.click
        } else {
          result.color = Colors.grey
          result.iconSVG = 'copy'
          result.tooltip = this.getTranslate('misc.buttons.copy')
          result.callback = () => {
            return navigator.clipboard.writeText(this.title)
          }
        }

        if (this.buttonCallback) {
          result.icon = icons.done
          result.iconSVG = undefined
          result.color = Colors.success
        }

        if (this.disabled) {
          result.disabled = this.disabled
        }
      }

      return result
    },

    $_directives() {
      const result = []
      if (this.tooltip || this.showTooltip) {
        result.push({
          name: components.tooltip,
          options: {
            value: this.tooltip || this.title,
            timeout: this.tooltipTimeout,
            placement: placements.top
          }
        })
      }

      return result
    },

    $_badges() {
      if (!this.badge || typeof this.badge !== 'object') {
        return []
      }

      const allowedPositions = [ 'left-top', 'right-top', 'right-bottom', 'left-bottom' ]
      const result = []

      for (const position of allowedPositions) {
        if (!Object.prototype.hasOwnProperty.call(this.badge, position)) {
          continue
        }

        const config = this.badge[position]
        if (!config || typeof config !== 'object') {
          continue
        }

        const { value, ...rest } = config
        if (value === undefined || value === null) {
          continue
        }

        const props = { ...rest }

        if (!Object.prototype.hasOwnProperty.call(props, 'color') || props.color === undefined) {
          props.color = this.color
        }

        if (!Object.prototype.hasOwnProperty.call(props, 'placement') || props.placement === undefined) {
          props.placement = resolveBadgePlacement(position)
        }

        if (Object.values(icons).includes(value)) {
          props.icon = value
        } else {
          props.value = value
        }

        result.push({
          position,
          props
        })
      }

      return result
    },

    title() {
      if (!isUUID(this.value) && typeof this.value !== 'object') {
        return this.value
      }

      const title = get(this.data, this.pathToTitle, '')

      if (!!title && this.translatePath) {
        return this.getTranslate(`${this.translatePath}.${title}`)
      }

      return title
    },

    hasTo() {
      return !!this.$_to && Object.keys(this.$_to).length > 0
    },
    hasIcon() {
      return !!this.icon || !!this.iconSVG || !!this.$scopedSlots.icon || !!this.$slots.icon
    },
    hasButton() {
      return !!this.button || this.copy
    },
    hasContent() {
      return !!this.title || !!this.label
    },
    hasAfter() {
      return !!(this.$scopedSlots.after || this.$slots.after)
    },

    gridTemplateColumns() {
      const result = []
      if (this.hasIcon) {
        result.push('auto')
      }
      result.push('1fr')

      return result.join(' ')
    }
  },

  watch: {
    value: {
      handler() {
        this.getData()
        this.setShowTooltip()
      },
      deep: true
    },

    tooltip() {
      this.setShowTooltip()
    }
  },

  mounted() {
    this.getData()
    this.setShowTooltip()

    window.addEventListener('resize', () => {
      return setTimeout(this.setShowTooltip, 200)
    })
  },

  updated() {
    this.setShowTooltip()
  },

  beforeDestroy() {
    window.removeEventListener('resize', () => {
      return setTimeout(this.setShowTooltip, 200)
    })
  },

  methods: {
    setShowTooltip() {
      const title = this.$refs.title
      if (title) {
        if (parseInt(title.offsetWidth) < parseInt(title.scrollWidth)) {
          this.showTooltip = true
        } else {
          this.showTooltip = false
        }
      }
    },

    async getData() {
      try {
        if (this.value) {
          if (isUUID(this.value)) {
            if (this.checkPermissions(`advanced.${getServiceNameViaPoint(this.service)}.get`, permissionPresets.meUp)) {
              this.loadingGet = true
              const key = [ projectName, this.service, 'get', this.value ].filter(Boolean).join(':')
              const data = await cachedGet(key, this.service, this.value, this.params)
              if (data) {
                this.data = data
              }
              this.loadingGet = false
            }
          } else if (typeof this.value === 'object') {
            this.data = this.value
          }
        }
      } catch (error) {
        this.loadingGet = false
      }
    },

    clickHandler(event) {
      if (this.onClick) {
        this.onClick(event)
      }
    },

    async buttonClickHandler(event) {
      event.stopPropagation()
      await this.$_button.callback()
      if (this.copy) {
        this.addSnackbar({
          text: this.getTranslate('misc.copied'),
          type: 'success'
        })
      }

      this.buttonCallback = true
      setTimeout(() => {
        this.buttonCallback = undefined
      }, 1000)
    }
  },

  render
}
